import { Localized } from "@fluent/react"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import DialogTitle from "@mui/material/DialogTitle"
import Rating from "@mui/material/Rating"
import Stack from "@mui/material/Stack"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import Box from "@mui/material/Box"
import { useState } from "react"
import { SaveButton } from "../../../app/components/SaveButton"
import { CancelButton } from "../../../app/components/CancelButton"
import { AppId } from "../../../app/appTypes"
import { useAppDispatch } from "../../../app/hooks"
import { showError, showSuccess } from "../../notifications/notificationsSlice"
import { saveSupplierRating, SupplierRating } from "../suppliersSlice"

type AddCollaborationOpinionDialogProps = {
    supplierId: AppId
    onSuccess: (opinion: SupplierRating) => void
    onCancel: () => void
}

export const AddCollaborationOpinionDialog = ({
    supplierId,
    onSuccess,
    onCancel,
}: AddCollaborationOpinionDialogProps) => {
    const dispatch = useAppDispatch()
    const [description, setDescription] = useState("")
    const [stars, setStars] = useState<number | null>(3)
    const [descriptionError, setDescriptionError] = useState(false)
    const [starsError, setStarsError] = useState(false)
    const [saving, setSaving] = useState<boolean>(false)

    const handleSave = async () => {
        setSaving(true)
        const invalidDescription = description.trim() === ""
        const invalidStars = !stars || stars < 1 || stars > 5
        setDescriptionError(invalidDescription)
        setStarsError(invalidStars)

        if (invalidDescription || invalidStars) {
            setSaving(false)
            return
        }

        const numericSupplierId = Number(supplierId)
        if (!Number.isFinite(numericSupplierId)) {
            dispatch(showError("error"))
            setSaving(false)
            return
        }

        try {
            const result = await dispatch(saveSupplierRating({
                supplierId: numericSupplierId,
                rating: stars,
                description: description,
            }))

            if (saveSupplierRating.fulfilled.match(result)) {
                dispatch(showSuccess("saved"))
                onSuccess(result.payload)
            } else {
                dispatch(showError("error"))
            }
        } catch (error) {
            dispatch(showError("error"))
        } finally {
            setSaving(false)
        }
    }

    return (
        <>
            <DialogTitle>
                <Localized id="supplier-rate-collaboration">
                    <span>_Dodaj opinię o współpracy</span>
                </Localized>
            </DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2} sx={{ pt: 1 }}>
                    <Box>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            <Localized id="rating">_Ocena</Localized>
                        </Typography>
                        <Rating
                            value={stars}
                            precision={1}
                            max={5}
                            onChange={(_, value) => setStars(value)}
                        />
                        {starsError && (
                            <Typography color="error" variant="caption" display="block">
                                <Localized id="field-is-required">_Pole jest wymagane</Localized>
                            </Typography>
                        )}
                    </Box>
                    <TextField
                        multiline
                        minRows={3}
                        fullWidth
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        error={descriptionError}
                        helperText={descriptionError ? <Localized id="field-is-required">_Pole jest wymagane</Localized> : undefined}
                        label={<Localized id="comment">_Komentarz</Localized>}
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                <SaveButton loading={saving} onClick={handleSave} />
                <CancelButton loading={saving} onClick={onCancel} />
            </DialogActions>
        </>
    )
}
